import React, { PropsWithChildren } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { PagePanel } from 'edenred-ui';
import { useTranslation } from 'react-i18next';

import { BenefitGroupForm } from '@epi-forms/BenefitGroup/BenefitGroupForm';
import {
  ICreateBenefitGroupForm,
  ICreateBenefitGroupFormProps
} from '@epi-models/pages/BenefitGroups';
import { useDispatch } from 'react-redux';
import { goToPage } from '@epi-actions/navigation';
import { routerPaths } from '@epi-constants/routerPaths';
import { useCreateBenefitGroupMutation } from '@epi-api/benefitGroups';
import { showNotification } from '@epi-actions/overlays';
import {
  CREATE_BENEFIT_GROUPS_CACHE_KEY,
  ERROR_RESPONSE_NOT_UNIQUE_NAME
} from '@epi-constants/benefitGroups';
import { formatValuesToDTO } from '@epi-helpers/BenefitGroupHelper';
import { useOnboarding } from '@epi-shared/hooks/useOnboarding';
import { ApiError } from '@epi-helpers/ApiError';

export const CreateBenefitGroupForm = ({
  defaultSettings,
  toggleModal,
  children
}: PropsWithChildren<ICreateBenefitGroupFormProps>) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [createBenefitGroup, { isLoading }] = useCreateBenefitGroupMutation({
    fixedCacheKey: CREATE_BENEFIT_GROUPS_CACHE_KEY
  });

  const { benefitGroupNotExists } = useOnboarding();

  const createBenefitGroupForm = useForm<ICreateBenefitGroupForm>({
    mode: 'onChange',
    defaultValues: defaultSettings
  });

  const checkedBenefits = createBenefitGroupForm.watch('benefitSettings');
  const isAnyBenefitChecked = Object.values(checkedBenefits).some(
    benefit => !!benefit
  );

  const backButton = benefitGroupNotExists
    ? undefined
    : {
        backText: t('containers.benefit_groups.buttons.cancel'),
        onBack: () => {
          dispatch(goToPage(routerPaths.benefitGroups));
        }
      };

  const handleSubmitForm = async formValues => {
    const formattedBenefitSettings = formatValuesToDTO(formValues);

    try {
      await createBenefitGroup({
        payload: formattedBenefitSettings
      }).unwrap();

      benefitGroupNotExists
        ? toggleModal(true)
        : dispatch(goToPage(routerPaths.benefitGroups));

      createBenefitGroupForm.reset(defaultSettings);
    } catch (error) {
      if (
        error instanceof ApiError &&
        error.prop?.errors[0]?.code === ERROR_RESPONSE_NOT_UNIQUE_NAME
      ) {
        createBenefitGroupForm.setError('groupName', {
          type: 'server',
          message: t('messages.benefit_group_already_exist')
        });
      } else {
        dispatch(showNotification({ message: 'messages.error' }, 'error'));
      }
    }
  };

  return (
    <FormProvider {...createBenefitGroupForm}>
      <BenefitGroupForm handleSubmitForm={handleSubmitForm}>
        <PagePanel
          hasOverflow={false}
          title={t('containers.benefit_groups.create_group_title')}
          footer={{
            submitText: t('containers.benefit_groups.buttons.add'),
            disableSubmit:
              !createBenefitGroupForm.formState.isValid ||
              !isAnyBenefitChecked ||
              isLoading,
            ...backButton
          }}
        >
          {children}
        </PagePanel>
      </BenefitGroupForm>
    </FormProvider>
  );
};
